import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, HeartPulse, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export function TenantHealthBoard() {
  const [scores, setScores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    // Get latest health score per company
    const { data, error } = await supabase
      .from('tenant_health_scores')
      .select('*')
      .order('computed_at', { ascending: false })
      .limit(200);

    if (error) {
      toast.error('Failed to load health scores');
    } else {
      // Deduplicate by company_id, keep latest
      const map = new Map<string, any>();
      (data || []).forEach((s) => {
        if (!map.has(s.company_id)) map.set(s.company_id, s);
      });
      setScores(Array.from(map.values()));
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  const gradeColor = (g: string) => {
    switch (g) {
      case 'green':
        return 'bg-emerald-500/10 text-emerald-600';
      case 'amber':
        return 'bg-amber-500/10 text-amber-600';
      case 'red':
        return 'bg-destructive/10 text-destructive';
      default:
        return '';
    }
  };

  const redCount = scores.filter((s) => s.grade === 'red').length;
  const amberCount = scores.filter((s) => s.grade === 'amber').length;
  const greenCount = scores.filter((s) => s.grade === 'green').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <HeartPulse className="h-5 w-5 text-primary" />
          Tenant Health Board
        </h2>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Healthy</div>
            <div className="text-2xl font-bold text-emerald-600 mt-1">{greenCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Warning</div>
            <div className="text-2xl font-bold text-amber-600 mt-1">{amberCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-muted-foreground">Critical</div>
            <div className="text-2xl font-bold text-destructive mt-1">{redCount}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Health Scores</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Issues</TableHead>
                  <TableHead>Computed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scores.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-sm">{s.company_id.slice(0, 8)}…</TableCell>
                    <TableCell className="font-bold">{s.score}/100</TableCell>
                    <TableCell>
                      <Badge className={gradeColor(s.grade)}>{s.grade}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[300px]">
                      {Array.isArray(s.reasons) ? s.reasons.join('; ') : '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(s.computed_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
                {scores.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No health scores computed yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
